"use client";

import Link from "next/link";
import { Facebook, Youtube, Twitter, Instagram } from "lucide-react";
import Image from "next/image";

// White footer body + brown bottom bar.
// Palette: headings #a8704e (brand copper), text/links #64483c (brand brown),
// hover #a8704e, dividers #eee4d9, bottom bar #64483c.

const FOOTER_LINKS = {
  "WHY SHOP WITH US": [
    { label: "Quality Assurance", href: "/" },
    { label: "Unbeatable Prices, Only Wholesale Prices", href: "/" },
    { label: "Satisfied National & International Buyer", href: "/" },
    { label: "More than 2Lakh Online Products", href: "/" },
    { label: "Same Day Shipped", href: "/" },
    { label: "Trusted Since 1995", href: "/" },
    { label: "Safe & Secure Shopping", href: "/" },
  ],
  "GET TO KNOW SILVER & GEM": [
    { label: "About Us", href: "/about-us" },
    { label: "Contact Us", href: "/contact-us" },
    { label: "Wholesale Enquiry", href: "/wholesale-enquiry" },
    { label: "Blogs", href: "/blog" },
  ],
  "FEATURED CATEGORIES": [
    { label: "Silver Rings", href: "/category/Silver-Rings" },
    { label: "Silver Pendants", href: "/category/Silver-Pendants" },
    { label: "Silver Earrings", href: "/category/Silver-Earrings" },
    { label: "Silver Chain", href: "/category/Silver-Chain" },
    { label: "Silver Jewellery Set", href: "/category/Silver-Jewellery-Set" },
    { label: "Silver Bracelets", href: "/category/Silver-Bracelets" },
    { label: "Silver Bangles", href: "/category/Silver-Bangles" },
    { label: "Silver Necklaces", href: "/category/Silver-Necklaces" },
  ],
  "CUSTOMER SERVICES": [
    { label: "Help & Support", href: "/support" },
    { label: "Terms of use", href: "/terms-conditions" },
    { label: "Return & Exchange Policy", href: "/exchange" },
    { label: "Shipping Policy", href: "/shipping-policy" },
    { label: "Privacy Policy", href: "/privacy-policy" },
  ],
};

const SOCIAL_LINKS = [
  { label: "Facebook", href: "https://www.facebook.com/silverandgemindia", icon: Facebook },
  { label: "Youtube", href: "https://www.youtube.com/@silverandgemexports", icon: Youtube },
  { label: "Instagram", href: "https://www.instagram.com/isilverandgem/", icon: Instagram },
  { label: "Twitter", href: "https://x.com/silverandgemexp", icon: Twitter },
];

const PAYMENT_METHODS = [
  { src: "/payment-icons/hdfc.png", alt: "HDFC" },
  { src: "/payment-icons/paypal.png", alt: "PayPal" },
  { src: "/payment-icons/visa.png", alt: "Visa" },
  { src: "/payment-icons/yesbank.png", alt: "Yes Bank" },
];

export default function Footer() {
  return (
    <footer className="bg-white border-t border-[#eee4d9]">
      {/* ── Main ── */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-10">
          {/* Logo, social, payments */}
          <div className="space-y-6 sm:col-span-2 lg:col-span-1">
            <Link href="/" className="inline-block" aria-label="Silver & Gem home">
              <Image
                src="/logo-png.png"
                alt="Silver & Gem Logo"
                width={60}
                height={60}
                className="w-20 h-auto"
              />
            </Link>

            <div className="flex items-center gap-2.5">
              {SOCIAL_LINKS.map((social) => (
                <Link
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-[#e3d5c6] text-[#64483c] transition-all duration-200 hover:border-[#a8704e] hover:bg-[#a8704e] hover:text-white"
                >
                  <social.icon className="h-4 w-4" />
                </Link>
              ))}
            </div>

            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#a8704e]">We Accept</p>
              <div className="flex flex-wrap items-center gap-2">
                {PAYMENT_METHODS.map((p) => (
                  <div
                    key={p.alt}
                    title={p.alt}
                    className="rounded-md border border-[#eee4d9] bg-white px-2 py-1.5"
                  >
                    <Image
                      src={p.src}
                      alt={p.alt}
                      width={48}
                      height={28}
                      className="h-6 w-auto object-contain"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(FOOTER_LINKS).map(([category, links]) => (
            <div key={category}>
              <h3 className="mb-4 text-[13px] font-bold uppercase tracking-wider text-[#a8704e]">
                {category}
              </h3>
              <ul className="space-y-2.5">
                {links.map((link) => (
                  <li key={link.label}>
                    {category === "WHY SHOP WITH US" ? (
                      <span className="block text-sm text-[#64483c]">{link.label}</span>
                    ) : (
                      <Link
                        href={link.href}
                        className="block text-sm text-[#64483c] transition-colors duration-200 hover:text-[#a8704e]"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div className="bg-[#64483c]">
        <div className="container mx-auto flex flex-col items-center justify-between gap-3 px-4 py-4 text-center sm:px-6 lg:flex-row lg:px-8 lg:text-left">
          <p className="text-[13px] text-white/85">
            © {new Date().getFullYear()} Silver &amp; Gem. All rights reserved.
          </p>
          <p className="text-[13px] italic text-white/85">
            Trusted by customers worldwide since 1995 • Exquisite handcrafted jewelry
          </p>
          <Link
            href="https://gosbfy.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-full border border-white/30 px-3 py-1 text-[12px] font-medium text-white/90 transition-colors duration-200 hover:bg-white hover:text-[#64483c]"
          >
            Made with Gosbfy
          </Link>
        </div>
      </div>
    </footer>
  );
}
